/**
 * Minimal observable store: no framework, no dependencies.
 *
 * Subscribers receive the new state after every mutation; `patch` accepts a
 * partial object or a reducer, so callers can never accidentally share mutable
 * state between views.
 */

export type Listener<T> = (state: T, previous: T) => void;

export interface Store<T extends object> {
  get(): Readonly<T>;
  set(patch: Partial<T> | ((state: Readonly<T>) => Partial<T>)): void;
  subscribe(listener: Listener<T>): () => void;
}

export const createStore = <T extends object>(initial: T): Store<T> => {
  let state: T = initial;
  const listeners = new Set<Listener<T>>();

  return {
    get: () => state,
    set: (patch) => {
      const previous = state;
      const next = typeof patch === 'function' ? patch(state) : patch;
      let changed = false;
      for (const key of Object.keys(next) as Array<keyof T>) {
        if (state[key] !== next[key]) {
          changed = true;
          break;
        }
      }
      if (!changed) return;
      state = { ...state, ...next };
      for (const listener of listeners) listener(state, previous);
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
};
